import { useEffect, useState } from "react";
import { Wallet, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";

export const CheckoutDialog = ({ plan, cycle, onClose }) => {
  const [method, setMethod] = useState("paypal");
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  useEffect(() => { setApplied(null); setCode(""); }, [plan, cycle]);
  const [cfg, setCfg] = useState({ paypal: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/payments/config").then((r) => setCfg(r.data)); }, []);

  if (!plan) return null;
  const base = cycle === "annual" ? plan.annual : plan.monthly;
  const amount = applied ? applied.amount : base;
  const applyCode = async () => {
    try {
      const { data } = await api.post("/coupons/validate", { code, plan: plan.id, cycle });
      if (data.type !== "percent") { setApplied(null); return toast.info("Este código regala meses: canjéalo en Cuenta → Canjear código"); }
      setApplied(data); toast.success(`Código ${data.code}: -${data.value}% aplicado`);
    } catch (e) { setApplied(null); toast.error(errMsg(e)); }
  };

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/paypal/create",
        { plan: plan.id, cycle, method: "paypal", origin_url: window.location.origin, coupon: applied?.code || null });
      if (data.free) { toast.success("¡Plan activado gratis con tu código!"); window.location.href = "/studio"; return; }
      window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e));
      setLoading(false);
    }
  };

  const opts = [
    { id: "paypal", icon: Wallet, title: "PayPal", sub: cfg.paypal ? "Paga de forma segura con PayPal" : "PayPal aún no está configurado", on: cfg.paypal },
  ];

  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-white/10 max-w-md" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-white">Plan {plan.name} · {cycle === "annual" ? "Anual" : "Mensual"}</DialogTitle>
          <DialogDescription>Total hoy: <span className="text-white font-semibold" data-testid="checkout-amount">{money(amount)} USD</span>{cycle === "annual" && (plan.annual_discount ?? 0.15) > 0 && ` (${Math.round((plan.annual_discount ?? 0.15) * 100)}% de descuento aplicado)`}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          {opts.map(({ id, icon: I, title, sub, on }) => (
            <button key={id} disabled={!on} data-testid={`pay-method-${id}`} onClick={() => setMethod(id)}
              className={`w-full text-left flex gap-4 items-center p-4 rounded-xl border transition-colors ${method === id ? "border-[#E8C47C] bg-[#E8C47C]/10" : "border-white/10 hover:border-white/20"} ${!on ? "opacity-40 cursor-not-allowed" : ""}`}>
              <I className="h-5 w-5 text-[#E8C47C]" />
              <div><p className="text-sm text-white font-medium">{title}</p><p className="text-xs text-zinc-500">{sub}</p></div>
            </button>
          ))}
        </div>
        <div className="flex gap-2 mt-2">
          <input data-testid="coupon-input" value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setApplied(null); }} placeholder="¿Tienes un código de descuento?"
            className="flex-1 h-10 rounded-lg bg-zinc-900/60 border border-white/10 px-3 text-sm font-mono-tc outline-none focus:border-[#E8C47C]" />
          <Button type="button" variant="outline" data-testid="coupon-apply-btn" disabled={!code} onClick={applyCode} className="border-white/10 h-10">Aplicar</Button>
        </div>
        {applied && <p className="text-xs text-emerald-300" data-testid="coupon-applied">Código {applied.code} aplicado: antes {money(base)}, ahora {money(amount)}</p>}
        <Button data-testid="checkout-pay-btn" disabled={loading || (amount > 0 && !cfg.paypal)} onClick={pay} className="w-full mt-4 rounded-full bg-[#E8C47C] hover:bg-[#F3D9A4] text-black font-semibold">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : amount === 0 ? "Activar gratis" : !cfg.paypal ? "PayPal no disponible" : `Pagar ${money(amount)}`}
        </Button>
        <p className="text-[11px] text-zinc-500 flex items-center gap-1.5 justify-center"><ShieldCheck className="h-3.5 w-3.5" />Pago seguro y cifrado. El plan se activa al instante.</p>
      </DialogContent>
    </Dialog>
  );
};
